/* 
   CloudSens — Global Functionality 
   Handles: Shopify AJAX Cart, Video Interaction, Modals, and Toasts
*/

// --- Shared State ---
let cartData = null;
let discountCode = localStorage.getItem('cloudsens_discount') || null;

// --- Initialize ---
document.addEventListener('DOMContentLoaded', () => {
    initVideoInteractions();
    fetchCart();
});

// --- 1. Video Interaction ---
// Reels: tap-to-play / pause on mobile + desktop, autoplay only when scrolled into view,
// only one reel plays at a time, double-tap (or long-press of unmuted area) toggles audio.
function initVideoInteractions() {
    const reelCards = document.querySelectorAll('.reel-card[data-reel]');
    const reelVideos = [];

    reelCards.forEach(card => {
        const video = card.querySelector('video');
        if (!video) return;
        reelVideos.push(video);

        // iOS / autoplay-friendly defaults
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        // Tap card → toggle play/pause for THIS reel; pause others.
        const toggle = (e) => {
            // ignore taps on inner controls if any
            e.preventDefault();
            if (video.paused) {
                reelVideos.forEach(v => { if (v !== video) { v.pause(); v.muted = true; } });
                video.muted = false; // explicit tap → play with sound (gesture allows it)
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
                card.classList.add('is-playing');
            } else {
                video.pause();
                card.classList.remove('is-playing');
            }
        };
        card.addEventListener('click', toggle);
        card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') toggle(e);
        });

        // Reflect playing state for CSS (hide play-btn when playing)
        video.addEventListener('play', () => card.classList.add('is-playing'));
        video.addEventListener('pause', () => card.classList.remove('is-playing'));

        // Desktop: hover preview without sound
        card.addEventListener('mouseenter', () => {
            if (window.matchMedia('(hover: hover)').matches) {
                video.play().catch(() => {});
            }
        });
        card.addEventListener('mouseleave', () => {
            if (window.matchMedia('(hover: hover)').matches) {
                if (!card.classList.contains('user-played')) {
                    video.pause();
                    video.currentTime = 0;
                }
            }
        });
    });

    // Mobile autoplay-when-visible (single reel plays at a time)
    if ('IntersectionObserver' in window && reelVideos.length) {
        const io = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const v = entry.target;
                if (entry.isIntersecting && entry.intersectionRatio > 0.65) {
                    reelVideos.forEach(other => { if (other !== v) other.pause(); });
                    v.muted = true; // autoplay-on-scroll must be muted to be allowed
                    v.play().catch(() => {});
                } else if (entry.intersectionRatio < 0.25) {
                    v.pause();
                }
            });
        }, { threshold: [0, 0.25, 0.65, 1] });
        reelVideos.forEach(v => io.observe(v));
    }

    // Hero video — keep muted autoplay, never block render
    const heroVideo = document.getElementById('hero-video');
    if (heroVideo) {
        heroVideo.muted = true;
        heroVideo.playsInline = true;
        const tryPlay = () => heroVideo.play().catch(() => {});
        tryPlay();
        // iOS sometimes needs a user gesture; resume on first tap anywhere
        document.addEventListener('touchstart', tryPlay, { once: true, passive: true });
    }
}


// --- 4. Shopify AJAX Cart ---
function fetchCart() {
    fetch(window.routes.cart_url + '.js')
        .then(response => response.json())
        .then(cart => {
            cartData = cart;
            updateCartUI();
        })
        .catch((e) => {
            console.error('Error fetching cart:', e);
        });
}

function updateCartUI() {
    if (!cartData) return;

    // Update cart count (header badge + drawer header both use .js-cart-count)
    document.querySelectorAll('.js-cart-count').forEach(el => {
        el.textContent = cartData.item_count;
    });
    // Update the "item"/"items" word in the drawer header
    document.querySelectorAll('.js-cart-word').forEach(el => {
        el.textContent = cartData.item_count === 1 ? 'item' : 'items';
    });

    // Update Side cart items list
    const itemsList = document.getElementById('cart-items');
    if (itemsList) {
        if (cartData.items.length === 0) {
            itemsList.innerHTML = '<p style="text-align:center; padding: 40px 0;">Your cart is empty.</p>';
        } else {
            itemsList.innerHTML = cartData.items.map(item => `
                <div class="cart-item">
                    <img src="${item.featured_image.url}" alt="${item.title}" />
                    <div class="cart-item-info">
                        <div class="name">${item.product_title}</div>
                        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="variant">${item.variant_title}</div>` : ''}
                        <div class="qty-controls">
                            <button class="qty-btn" onclick="updateCartItem('${item.key}', ${item.quantity - 1})">−</button>
                            <span class="qty-num">${item.quantity}</span>
                            <button class="qty-btn" onclick="updateCartItem('${item.key}', ${item.quantity + 1})">+</button>
                        </div>
                    </div>
                    <div class="cart-item-price">${formatMoney(item.line_price)}</div>
                </div>
            `).join('');
        }
    }

    // Update Totals — both the Subtotal row and the grand Total row
    document.querySelectorAll('.js-cart-subtotal').forEach(el => {
        el.innerHTML = formatMoney(cartData.total_price);
    });
    const subtotalElements = document.querySelectorAll('.cart-total-row.grand span:last-child');
    subtotalElements.forEach(el => {
        el.innerHTML = formatMoney(cartData.total_price);
    });

    updateShippingProgress();
    updateUpsellVisibility();
}

function updateShippingProgress() {
    const container = document.getElementById('cart-progress');
    if (!container || !cartData) return;

    const bar = document.getElementById('cart-progress-bar');
    const text = document.getElementById('cart-progress-text');
    const thresholdCents = parseInt(container.dataset.threshold, 10) || 10000;
    const totalCents = cartData.total_price || 0;
    const ratio = thresholdCents > 0 ? Math.min(totalCents / thresholdCents, 1) : 0;
    const percent = Math.max(0, Math.min(100, ratio * 100));

    if (bar) bar.style.width = percent + '%';

    if (text) {
        if (totalCents <= 0) {
            text.innerHTML = `You're <strong>${formatMoney(thresholdCents)} away</strong> from free delivery! 🎁`;
        } else if (totalCents >= thresholdCents) {
            text.innerHTML = `🎉 You've unlocked <strong>FREE delivery</strong>!`;
        } else {
            const remaining = thresholdCents - totalCents;
            text.innerHTML = `You're <strong>${formatMoney(remaining)} away</strong> from free delivery! 🎁`;
        }
    }

    container.classList.toggle('is-complete', totalCents >= thresholdCents);
}

function updateUpsellVisibility() {
    const upsell = document.getElementById('cart-upsell');
    if (!upsell || !cartData) return;

    const productId = parseInt(upsell.dataset.productId, 10);
    const variantIds = (upsell.dataset.variantIds || '')
        .split(',')
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

    const inCart = cartData.items.some(item =>
        item.product_id === productId || variantIds.includes(item.variant_id || item.id)
    );

    upsell.style.display = inCart ? 'none' : '';
}

function updateCartItem(lineKey, quantity) {
    const body = JSON.stringify({
        id: lineKey,
        quantity: quantity
    });

    fetch(window.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body
    })
    .then(response => response.json())
    .then(cart => {
        cartData = cart;
        updateCartUI();
    })
    .catch((e) => {
        console.error('Error updating cart:', e);
        showToast(window.cartStrings.error);
    });
}

// Global handle for adding to cart (called from product forms)
function handleAddToCart(formElement) {
    const formData = new FormData(formElement);

    fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
    })
    .then(response => {
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }
        return response.json();
    })
    .then(item => {
        fetchCart(); // Refresh cart data
        openCart();  // Open drawer
        showToast('Item added to cart');
    })
    .catch((error) => {
        console.error('Error:', error);
        showToast(window.cartStrings.error);
    });
}

function openCart() {
    const overlay = document.getElementById('cart-overlay');
    const drawer = document.getElementById('cart-drawer');
    if (overlay) overlay.classList.add('open');
    if (drawer) drawer.classList.add('open');
}

function closeCart() {
    const overlay = document.getElementById('cart-overlay');
    const drawer = document.getElementById('cart-drawer');
    if (overlay) overlay.classList.remove('open');
    if (drawer) drawer.classList.remove('open');
}

// Utility for money formatting (basic implementation)
function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
}

// --- Utils ---
function showToast(msg) {
    const existing = document.querySelector('.toast');
    if (existing) existing.remove();
    
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.classList.add('visible'), 100);
    setTimeout(() => {
        toast.classList.remove('visible');
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}



document.addEventListener('DOMContentLoaded', () => {
  // Scroll reveal
  const reveals = document.querySelectorAll('.reveal');
  const revealOnScroll = () => {
    reveals.forEach(reveal => {
      const windowHeight = window.innerHeight;
      const elementTop = reveal.getBoundingClientRect().top;
      const elementVisible = 50;
      if (elementTop < windowHeight - elementVisible) {
        reveal.classList.add('visible');
      }
    });
  };
  window.addEventListener('scroll', revealOnScroll, { passive: true });
  window.addEventListener('resize', revealOnScroll, { passive: true });
  window.addEventListener('load', revealOnScroll);
  revealOnScroll(); // Trigger immediately on load

  // Failsafe: if scroll events never fire (very tall first viewport, a thrown
  // handler, etc.), force every reveal element visible after a short delay so
  // content can never stay permanently invisible.
  setTimeout(() => {
    reveals.forEach(reveal => reveal.classList.add('visible'));
  }, 4000);
});
